import { type HTMLAttributes, type DetailedHTMLProps } from 'react';
import { type StyledComponent } from '@emotion/styled';
import { styled } from '@mui/material/styles';
import { useTheme } from '../../../../theme';
import { type CSSObject } from '@emotion/react';

type StyledIcon = StyledComponent<
  object,
  DetailedHTMLProps<HTMLAttributes<HTMLSpanElement>, HTMLSpanElement>,
  object
>;

const createSvgUrl = (svgContent: string): string =>
  `url('data:image/svg+xml,${encodeURIComponent(svgContent)}')`;

const indeterminateSvgContent = (color: string) => `
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="7" width="10" height="1.5" rx="0.75" fill="${color}"/>
  </svg>
`;

const checkedSvgContent = (color: string) => `
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12.76 5.20851C12.4533 4.91814 11.9706 4.93285 11.6819 5.24137L6.83175 10.4245L4.33463 7.578C4.05593 7.26032 3.57395 7.23002 3.25809 7.51033C2.94224 7.79064 2.91211 8.27541 3.19081 8.5931L5.74946 11.5097C6.31074 12.1495 7.29741 12.1654 7.87883 11.5441L12.7927 6.29289C13.0814 5.98437 13.0668 5.49888 12.76 5.20851Z" fill="${color}"/>
  </svg>
`;

export const CheckboxDefault: StyledIcon = styled('span')((): CSSObject => {
  const theme = useTheme();
  return {
    position: 'relative',
    width: 16,
    height: 16,
    border: `1px solid ${theme.checkbox.default.default}`,
    borderRadius: theme.shape.radius4,
    '&:before': {
      position: 'absolute',
      height: 16,
      width: 16,
      left: 0,
      top: 0,
      content: '""',
    },
    'input:hover ~ &': {
      borderColor: theme.checkbox.default.hover,
    },
    'input:active ~ &': {
      borderColor: theme.checkbox.default.pressed,
    },
    'input:focus ~ &': {
      outline: `2px solid ${theme.border.interactive.defaultFocused}`,
      borderColor: theme.checkbox.default.hover,
    },
    'input:disabled ~ &': {
      borderColor: theme.checkbox.disabled.border,
      background: theme.checkbox.disabled.background,
    },
  };
});

export const CheckboxChecked: StyledIcon = styled(CheckboxDefault)(
  (): CSSObject => {
    const theme = useTheme();

    return {
      border: 'none',
      backgroundColor: theme.checkbox.active.default,
      '&:before': {
        backgroundImage: createSvgUrl(
          checkedSvgContent(theme.checkbox.selector.default),
        ),
      },
      'input:hover ~ &': {
        borderColor: theme.checkbox.active.hover,
      },
      'input:active ~ &': {
        backgroundColor: theme.checkbox.active.pressed,
      },
      'input:focus ~ &': {
        outline: `2px solid ${theme.border.interactive.defaultFocused}`,
        backgroundColor: theme.checkbox.active.default,
      },
      'input:disabled ~ &': {
        backgroundColor: theme.checkbox.active.disabled,
        '&:before': {
          backgroundImage: createSvgUrl(
            checkedSvgContent(theme.checkbox.selector.disabled),
          ),
        },
      },
    };
  },
);

export const CheckboxIndeterminate: StyledIcon = styled(CheckboxDefault)(
  (): CSSObject => {
    const theme = useTheme();

    return {
      border: 'none',
      backgroundColor: theme.checkbox.active.default,
      '&:before': {
        backgroundImage: createSvgUrl(
          indeterminateSvgContent(theme.checkbox.selector.default),
        ),
      },
      'input:hover ~ &': {
        borderColor: theme.checkbox.active.hover,
      },
      'input:active ~ &': {
        backgroundColor: theme.checkbox.active.pressed,
      },
      'input:focus ~ &': {
        outline: `2px solid ${theme.border.interactive.defaultFocused}`,
        backgroundColor: theme.checkbox.active.default,
      },
      'input:disabled ~ &': {
        backgroundColor: theme.checkbox.active.disabled,
        '&:before': {
          backgroundImage: createSvgUrl(
            indeterminateSvgContent(theme.checkbox.selector.disabled),
          ),
        },
      },
    };
  },
);

export const CheckboxHasError: StyledIcon = styled(CheckboxDefault)(
  (): CSSObject => {
    const theme = useTheme();
    return {
      borderColor: theme.checkbox.error.error,
      'input:hover ~ &': {
        borderColor: theme.checkbox.error.hover,
      },
      'input:active ~ &': {
        borderColor: theme.checkbox.error.pressed,
      },
      'input:focus ~ &': {
        outline: `2px solid ${theme.palette.alert.negative[100]}`,
        borderColor: theme.checkbox.error.error,
      },
    };
  },
);
